import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const WorkLocation = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingLocation, setViewingLocation] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [formData, setFormData] = useState({
    locationName: '',
    latitude: '',
    longitude: '',
    ipRanges: '' // Store as string, will be converted to array
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, search, sortBy, sortOrder]);

  const fetchLocations = async () => {
    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    setLoading(true);
    try {
      const currentUserId = localStorage.getItem('id');
      
      if (!currentUserId) {
        toast.error('User ID not found. Please login again.');
        return;
      }

      const payload = {
        action: "get-work-locations",
        search: search,
        status: "active",
        page: pagination.currentPage,
        limit: pagination.limit,
        ...(sortBy && { sortBy: sortBy, sortOrder: sortOrder }),
        currentUserId: currentUserId
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        setLocations(result.data || []);
        setPagination(prev => ({
          ...prev,
          currentPage: result.page || prev.currentPage,
          totalPages: result.totalPages || prev.totalPages,
          totalCount: result.total || prev.totalCount,
          hasNextPage: (result.page || prev.currentPage) < (result.totalPages || prev.totalPages),
          hasPreviousPage: (result.page || prev.currentPage) > 1
        }));
      } else {
        toast.error(result?.message || 'Failed to fetch work locations');
      }
    } catch (error) {
      console.error('Error fetching work locations:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const getSerialNumber = (index) => {
    return (pagination.currentPage - 1) * pagination.limit + index + 1;
  };

  const handleAdd = () => {
    setIsEditMode(false);
    setEditingLocationId(null);
    setFormData({
      locationName: '',
      latitude: '',
      longitude: '',
      ipRanges: ''
    });
    setFormErrors({});
    setShowAddModal(true);
  };

  const handleEdit = (location) => {
    setIsEditMode(true);
    setEditingLocationId(location.id);
    // Convert ipRanges array to string (one per line or comma-separated)
    const ipRangesString = location.ipRanges && Array.isArray(location.ipRanges) 
      ? location.ipRanges.join('\n') 
      : '';
    setFormData({
      locationName: location.locationName || '',
      latitude: location.latitude?.toString() || '',
      longitude: location.longitude?.toString() || '',
      ipRanges: ipRangesString
    });
    setFormErrors({});
    setShowAddModal(true);
  };

  const handleView = (location) => {
    setViewingLocation(location);
    setShowViewModal(true);
  };

  const handleDelete = (location) => {
    setLocationToDelete(location);
    setShowDeleteModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setIsEditMode(false);
    setEditingLocationId(null);
    setFormData({
      locationName: '',
      latitude: '',
      longitude: '',
      ipRanges: ''
    });
    setFormErrors({});
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewingLocation(null);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setLocationToDelete(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.locationName.trim()) {
      newErrors.locationName = 'Location name is required';
    }

    if (!formData.latitude.trim()) {
      newErrors.latitude = 'Latitude is required';
    } else if (isNaN(parseFloat(formData.latitude))) {
      newErrors.latitude = 'Latitude must be a valid number';
    }

    if (!formData.longitude.trim()) {
      newErrors.longitude = 'Longitude is required';
    } else if (isNaN(parseFloat(formData.longitude))) {
      newErrors.longitude = 'Longitude must be a valid number';
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const currentUserId = localStorage.getItem('id');
    
    if (!currentUserId) {
      toast.error('User ID not found. Please login again.');
      return;
    }

    setLoading(true);
    try {
      // Parse IP ranges from string to array
      // Support both newline-separated and comma-separated formats
      let ipRangesArray = [];
      if (formData.ipRanges && formData.ipRanges.trim()) {
        ipRangesArray = formData.ipRanges
          .split(/[\n,]/) // Split by newline or comma
          .map(range => range.trim()) // Trim whitespace
          .filter(range => range.length > 0); // Remove empty strings
      }

      const payload = {
        action: isEditMode ? 'update-work-location' : 'add-work-location',
        locationName: formData.locationName.trim(),
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        currentUserId: currentUserId,
        status: 'active',
        ...(ipRangesArray.length > 0 && { ipRanges: ipRangesArray }),
        ...(isEditMode && { id: editingLocationId })
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result?.message || (isEditMode ? 'Work location updated successfully' : 'Work location created successfully'));
        closeAddModal();
        fetchLocations();
      } else {
        toast.error(result?.message || (isEditMode ? 'Failed to update work location' : 'Failed to create work location'));
      }
    } catch (error) {
      console.error('Error saving work location:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!locationToDelete) return;

    const currentUserId = localStorage.getItem('id');
    
    if (!currentUserId) {
      toast.error('User ID not found. Please login again.');
      closeDeleteModal();
      return;
    }

    setLoading(true);
    try {
      const payload = {
        action: 'delete-work-location',
        id: locationToDelete.id,
        currentUserId: currentUserId
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result?.message || 'Work location deleted successfully');
        closeDeleteModal();
        fetchLocations();
      } else {
        toast.error(result?.message || 'Failed to delete work location');
      }
    } catch (error) {
      console.error('Error deleting work location:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusLower = status?.toLowerCase();
    let className = 'employee-status';
    let text = status?.toUpperCase() || 'ACTIVE';

    switch (statusLower) {
      case 'active':
        className += ' employee-status-active';
        break;
      case 'inactive':
        className += ' employee-status-inactive';
        break;
      default:
        className += ' employee-status-active';
    }

    return <span className={className}>{text}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const day = date.getDate().toString().padStart(2, '0');
        const month = date.toLocaleString('default', { month: 'short' });
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
      }
      return dateString;
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          {/* Header with Title */}
          <div className="row mb-3 employee-header-row">
            <div className="col-md-8">
              <h1 className="employee-title">
                Work Location Setup
                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                Manage and configure your company's branch and regional office locations.
              </p>
            </div>
            <div className="col-md-4" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', marginTop: '10px' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAdd}
                style={{ padding: '8px 20px', fontSize: '14px' }}
              >
                <i className="fas fa-plus mr-2"></i>
                Add
              </button>
            </div>
          </div>

          {/* Work Location Table Card */}
          <div className="card employee-content-card">
            <div className="card-body">
              <div className="employee-table-container">
                {/* Search */}
                <div className="employee-search-container" style={{ marginBottom: '20px' }}>
                  <div className="form-group" style={{ marginBottom: '15px' }}>
                    <input
                      type="text"
                      className="form-input employee-search-input"
                      placeholder="Search by location name..."
                      value={search}
                      onChange={handleSearch}
                    />
                  </div>
                </div>

                {loading ? (
                  <Loading size={120} />
                ) : (
                  <>
                    <div className="employee-table-wrapper">
                      <table className="employee-table">
                        <thead>
                          <tr>
                            <th className="employee-sortable">S.No</th>
                            <th
                              className="employee-sortable"
                              onClick={() => handleSort('locationName')}
                            >
                              Location Name
                              {sortBy === 'locationName' && (
                                <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                              )}
                              {sortBy !== 'locationName' && (
                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                              )}
                            </th>
                            <th>Latitude</th>
                            <th>Longitude</th>
                            <th>IP Ranges</th>
                            <th
                              className="employee-sortable"
                              onClick={() => handleSort('status')}
                            >
                              Status
                              {sortBy === 'status' && (
                                <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ml-1`}></i>
                              )}
                              {sortBy !== 'status' && (
                                <i className="fas fa-sort ml-1 employee-sort-icon"></i>
                              )}
                            </th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {locations.length === 0 ? (
                            <tr>
                              <td colSpan="7" className="employee-empty">
                                No work locations found
                              </td>
                            </tr>
                          ) : (
                            locations.map((location, index) => {
                              const hasIPRanges = location.ipRanges && Array.isArray(location.ipRanges) && location.ipRanges.length > 0;
                              return (
                                <tr key={location.id}>
                                  <td>{getSerialNumber(index)}</td>
                                  <td>{location.locationName || '-'}</td>
                                  <td>{location.latitude || '-'}</td>
                                  <td>{location.longitude || '-'}</td>
                                  <td>
                                    {hasIPRanges ? (
                                      <span style={{ color: '#155724', fontSize: '12px' }}>
                                        <i className="fas fa-check-circle mr-1"></i>
                                        {location.ipRanges.length} range(s)
                                      </span>
                                    ) : (
                                      <span style={{ color: '#6c757d', fontSize: '12px', fontStyle: 'italic' }}>
                                        Not configured
                                      </span>
                                    )}
                                  </td>
                                  <td>{getStatusBadge(location.status)}</td>
                                  <td>
                                    <div className="employee-actions">
                                      <button
                                        className="employee-action-btn employee-action-view"
                                        onClick={() => handleView(location)}
                                        title="View"
                                      >
                                        <i className="fa-solid fa-eye"></i>
                                      </button>
                                      <button
                                        className="employee-action-btn employee-action-edit"
                                        onClick={() => handleEdit(location)}
                                        title="Edit"
                                      >
                                        <i className="fa-solid fa-pencil"></i>
                                      </button>
                                      <button
                                        className="employee-action-btn employee-action-delete"
                                        onClick={() => handleDelete(location)}
                                        title="Delete"
                                      >
                                        <i className="fa-solid fa-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    {locations.length > 0 && pagination.totalPages > 0 && (
                      <div className="employee-pagination">
                        <button
                          className="pagination-btn"
                          onClick={() => handlePageChange(1)}
                          disabled={pagination.currentPage === 1}
                        >
                          «
                        </button>
                        <button
                          className="pagination-btn"
                          onClick={() => handlePageChange(pagination.currentPage - 1)}
                          disabled={pagination.currentPage === 1}
                        >
                          ‹
                        </button>
                        {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                          <button
                            key={page}
                            className={`pagination-btn ${pagination.currentPage === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        ))}
                        <button
                          className="pagination-btn"
                          onClick={() => handlePageChange(pagination.currentPage + 1)}
                          disabled={pagination.currentPage === pagination.totalPages}
                        >
                          ›
                        </button>
                        <button
                          className="pagination-btn"
                          onClick={() => handlePageChange(pagination.totalPages)}
                          disabled={pagination.currentPage === pagination.totalPages}
                        >
                          »
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeAddModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{isEditMode ? 'Edit Work Location' : 'Add Work Location'}</h3>
              <button className="modal-close" onClick={closeAddModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="locationName" className="form-label">
                    Location Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="locationName"
                    className={`form-input ${formErrors.locationName ? 'error' : ''}`}
                    placeholder="Enter location name"
                    value={formData.locationName}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, locationName: e.target.value }));
                      if (formErrors.locationName) {
                        setFormErrors(prev => ({ ...prev, locationName: '' }));
                      }
                    }}
                    required
                  />
                  {formErrors.locationName && (
                    <span className="error-message">{formErrors.locationName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="latitude" className="form-label">
                    Latitude <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    id="latitude"
                    className={`form-input ${formErrors.latitude ? 'error' : ''}`}
                    placeholder="Enter latitude (e.g., 13.01510)"
                    value={formData.latitude}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, latitude: e.target.value }));
                      if (formErrors.latitude) {
                        setFormErrors(prev => ({ ...prev, latitude: '' }));
                      }
                    }}
                    required
                  />
                  {formErrors.latitude && (
                    <span className="error-message">{formErrors.latitude}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="longitude" className="form-label">
                    Longitude <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    id="longitude"
                    className={`form-input ${formErrors.longitude ? 'error' : ''}`}
                    placeholder="Enter longitude (e.g., 80.21069)"
                    value={formData.longitude}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, longitude: e.target.value }));
                      if (formErrors.longitude) {
                        setFormErrors(prev => ({ ...prev, longitude: '' }));
                      }
                    }}
                    required
                  />
                  {formErrors.longitude && (
                    <span className="error-message">{formErrors.longitude}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="ipRanges" className="form-label">
                    IP Ranges <span style={{ fontSize: '12px', color: '#6c757d' }}>(optional)</span>
                  </label>
                  <textarea
                    id="ipRanges"
                    className="form-input"
                    rows="4"
                    placeholder="Enter IP ranges (one per line or comma-separated)&#10;Example:&#10;192.168.1.0/24&#10;192.168.1.100&#10;192.168.1.1-192.168.1.50"
                    value={formData.ipRanges}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, ipRanges: e.target.value }));
                    }}
                    style={{ resize: 'vertical', fontFamily: 'monospace', fontSize: '13px' }}
                  />
                  <small className="text-muted" style={{ display: 'block', marginTop: '5px', fontSize: '11px' }}>
                    <i className="fas fa-info-circle mr-1"></i>
                    Supports CIDR notation (192.168.1.0/24), exact IP (192.168.1.100), or IP range (192.168.1.1-192.168.1.50).
                    One per line or comma-separated.
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeAddModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Work Location' : 'Add Work Location')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewingLocation && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Work Location Details</h3>
              <button className="modal-close" onClick={closeViewModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group-compact">
                <label className="form-label-compact">Location Name</label>
                <div className="form-input-compact" style={{ backgroundColor: '#f5f5f5', padding: '8px 12px' }}>
                  {viewingLocation.locationName || '-'}
                </div>
              </div>
              <div className="form-group-compact">
                <label className="form-label-compact">Latitude</label>
                <div className="form-input-compact" style={{ backgroundColor: '#f5f5f5', padding: '8px 12px' }}>
                  {viewingLocation.latitude || '-'}
                </div>
              </div>
              <div className="form-group-compact">
                <label className="form-label-compact">Longitude</label>
                <div className="form-input-compact" style={{ backgroundColor: '#f5f5f5', padding: '8px 12px' }}>
                  {viewingLocation.longitude || '-'}
                </div>
              </div>
              <div className="form-group-compact">
                <label className="form-label-compact">IP Ranges</label>
                <div className="form-input-compact" style={{ backgroundColor: '#f5f5f5', padding: '8px 12px', fontFamily: 'monospace', fontSize: '12px' }}>
                  {viewingLocation.ipRanges && Array.isArray(viewingLocation.ipRanges) && viewingLocation.ipRanges.length > 0 ? (
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      {viewingLocation.ipRanges.map((range, index) => (
                        <li key={index}>{range}</li>
                      ))}
                    </ul>
                  ) : (
                    <span style={{ color: '#6c757d', fontStyle: 'italic' }}>No IP ranges configured</span>
                  )}
                </div>
              </div>
              {/* <div className="form-group-compact">
                <label className="form-label-compact">Status</label>
                <div style={{ padding: '8px 12px' }}>
                  {getStatusBadge(viewingLocation.status)}
                </div>
              </div> */}
              {viewingLocation.createdAt && (
                <div className="form-group-compact">
                  <label className="form-label-compact">Created At</label>
                  <div className="form-input-compact" style={{ backgroundColor: '#f5f5f5', padding: '8px 12px' }}>
                    {formatDate(viewingLocation.createdAt)}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  if (viewingLocation.latitude && viewingLocation.longitude) {
                    navigate(`/work-location-map/${viewingLocation.id}`, {
                      state: {
                        locationName: viewingLocation.locationName,
                        latitude: viewingLocation.latitude,
                        longitude: viewingLocation.longitude
                      }
                    });
                    closeViewModal();
                  } else {
                    toast.error('Location coordinates are missing');
                  }
                }}
                style={{ backgroundColor: '#0094a9', borderColor: '#0094a9', marginRight: '10px' }}
              >
                <i className="fas fa-map-marker-alt mr-2"></i>
                View on Map
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeViewModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Work Location</h3>
              <button className="modal-close" onClick={closeDeleteModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the work location <strong>{locationToDelete?.locationName}</strong>?</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeDeleteModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmDelete}
                disabled={loading}
                style={{ backgroundColor: '#ef4444', borderColor: '#ef4444' }}
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkLocation;
